// src/pages/profile/ProgressPage.jsx

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getPhaseData } from '../../data/programData';
import { 
  TrendingUp, 
  CheckCircle, 
  Circle, 
  Dumbbell,
  ClipboardCheck,
  Trophy,
  Calendar,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function ProgressPage() {
  const { user, aclCare } = useAuth();
  const navigate = useNavigate();
  
  const [completedSessions, setCompletedSessions] = useState([]);
  const [completedTestOuts, setCompletedTestOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  // Fetch history data
  useEffect(() => {
    async function fetchData() {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        const [sessionsResult, testOutsResult] = await Promise.all([
          api.getTrainingSessions(user.id),
          api.getTestOuts(user.id),
        ]);

        setCompletedSessions(sessionsResult.items || sessionsResult || []);
        setCompletedTestOuts(testOutsResult.items || testOutsResult || []);
      } catch (err) {
        console.error('[ProgressPage] Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [user?.id]);

  // Calculate overall stats
  const stats = useMemo(() => {
    const totalSessions = completedSessions.length;
    const totalTestOuts = completedTestOuts.length;
    const passedTestOuts = completedTestOuts.filter(t => t.overall_passed).length;
    
    // Calculate total workout time
    const totalMinutes = completedSessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Current streak (consecutive days with activity)
    let streak = 0;
    if (completedSessions.length > 0) {
      const sortedDates = [...new Set(
        completedSessions.map(s => new Date(Number(s.completed_at)).toDateString())
      )].sort((a, b) => new Date(b) - new Date(a));

      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      
      if (sortedDates[0] === today || sortedDates[0] === yesterday) {
        streak = 1;
        for (let i = 1; i < sortedDates.length; i++) {
          const curr = new Date(sortedDates[i - 1]);
          const prev = new Date(sortedDates[i]);
          const diffDays = Math.floor((curr - prev) / 86400000);
          if (diffDays === 1) {
            streak++;
          } else {
            break;
          }
        }
      }
    }

    return {
      totalSessions,
      totalTestOuts,
      passedTestOuts,
      totalTime: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
      streak,
    };
  }, [completedSessions, completedTestOuts]);

  // Group sessions by phase
  const sessionsByPhase = useMemo(() => {
    const grouped = { 1: [], 2: [], 3: [], 4: [] };
    completedSessions.forEach(session => {
      if (grouped[session.phase]) {
        grouped[session.phase].push(session);
      }
    });
    // Sort each phase's sessions by date descending
    Object.keys(grouped).forEach(phase => {
      grouped[phase].sort((a, b) => Number(b.completed_at) - Number(a.completed_at));
    });
    return grouped;
  }, [completedSessions]);

  // Phase data
  const phases = [
    { key: 'phase_1', num: 1, label: 'Phase I', color: 'blue' },
    { key: 'phase_2', num: 2, label: 'Phase II', color: 'green' },
    { key: 'phase_3', num: 3, label: 'Phase III', color: 'purple' },
    { key: 'phase_4', num: 4, label: 'Phase IV', color: 'orange' },
  ];

  const getPhaseProgress = (phaseKey, phaseNum) => {
    const phaseData = aclCare?.phases?.[phaseKey];
    const programPhaseData = getPhaseData(phaseNum);
    const targetCompletions = programPhaseData?.targetCompletions || { session_1: 3, session_2: 3 };
    
    const completions = {
      session_1: phaseData?.completions?.session_1 || 0,
      session_2: phaseData?.completions?.session_2 || 0,
      optional: phaseData?.completions?.optional || 0,
    };
    
    const completed = completions.session_1 + completions.session_2;
    const total = targetCompletions.session_1 + targetCompletions.session_2;
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      completions,
      targetCompletions,
    };
  };

  // Recent activity
  const recentActivity = useMemo(() => {
    const allActivity = [
      ...completedSessions.map(s => ({
        type: 'session',
        date: s.completed_at,
        phase: s.phase,
        workout: s.workout,
        data: s,
      })),
      ...completedTestOuts.map(t => ({
        type: 'testout',
        date: t.completed_at,
        phase: t.phase,
        passed: t.overall_passed,
        data: t,
      })),
    ];

    return allActivity
      .sort((a, b) => Number(b.date) - Number(a.date))
      .slice(0, 10);
  }, [completedSessions, completedTestOuts]);

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', fill: 'bg-blue-500' },
    green: { bg: 'bg-green-100', text: 'text-green-600', fill: 'bg-green-500' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', fill: 'bg-purple-500' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', fill: 'bg-orange-500' },
  };

  const togglePhase = (phaseNum) => {
    setExpandedPhase(expandedPhase === phaseNum ? null : phaseNum);
    setExpandedSession(null);
  };

  const toggleSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getWorkoutLabel = (workout) => {
    switch (workout) {
      case 1: return 'Session 1';
      case 2: return 'Session 2';
      case 3: return 'Optional';
      default: return 'Session';
    }
  };

  const getWorkoutColor = (workout) => {
    switch (workout) {
      case 1: return 'bg-blue-100 text-blue-700';
      case 2: return 'bg-green-100 text-green-700';
      case 3: return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <TrendingUp className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">My Progress</h1>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <Dumbbell className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.totalSessions}</p>
          <p className="text-xs text-gray-500">Workouts</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <ClipboardCheck className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.passedTestOuts}</p>
          <p className="text-xs text-gray-500">Tests Passed</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <Calendar className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.totalTime}</p>
          <p className="text-xs text-gray-500">Total Time</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <Trophy className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.streak}</p>
          <p className="text-xs text-gray-500">Day Streak</p>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Phase Progress</h2>
        </div>

        <div className="divide-y divide-gray-100">
          {phases.map((phase) => {
            const phaseData = aclCare?.phases?.[phase.key];
            const progress = getPhaseProgress(phase.key, phase.num);
            const isUnlocked = phaseData?.unlocked;
            const isPassed = phaseData?.test_out_passed;
            const isCurrent = aclCare?.current_phase === phase.num;
            const colors = colorClasses[phase.color];
            const isExpanded = expandedPhase === phase.num;
            const phaseSessions = sessionsByPhase[phase.num] || [];

            return (
              <div key={phase.key}>
                {/* Phase Header */}
                <button
                  onClick={() => isUnlocked && togglePhase(phase.num)}
                  disabled={!isUnlocked}
                  className={`
                    w-full p-4 text-left transition-colors
                    ${isUnlocked ? 'hover:bg-gray-50' : 'opacity-50'}
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      {isPassed ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      ) : isUnlocked ? (
                        <div className={`h-5 w-5 rounded-full mr-2 ${colors.bg} flex items-center justify-center`}>
                          <div className={`h-2 w-2 rounded-full ${colors.fill}`} />
                        </div>
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300 mr-2" />
                      )}
                      <span className="font-medium text-gray-900">{phase.label}</span>
                      {isCurrent && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      {isUnlocked && (
                        <>
                          <span className="text-sm text-gray-500 mr-2">
                            {progress.completed}/{progress.total}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {isUnlocked && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${isPassed ? 'bg-green-500' : colors.fill}`}
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  )}

                  {isUnlocked && (
                    <div className="mt-2 flex gap-4 text-xs text-gray-500">
                      <span>Session 1: {progress.completions.session_1}/{progress.targetCompletions.session_1}</span>
                      <span>Session 2: {progress.completions.session_2}/{progress.targetCompletions.session_2}</span>
                      <span>Optional: {progress.completions.optional}*</span>
                    </div>
                  )}
                </button>

                {/* Expanded Phase Content */}
                {isExpanded && isUnlocked && (
                  <div className="bg-gray-50 border-t border-gray-100">
                    {/* Go to Phase Link */}
                    <button
                      onClick={() => navigate(`/program/phase/${phase.num}`)}
                      className="w-full px-4 py-3 flex items-center justify-between text-primary hover:bg-gray-100 border-b border-gray-200"
                    >
                      <span className="text-sm font-medium">Go to {phase.label}</span>
                      <ExternalLink className="h-4 w-4" />
                    </button>

                    {/* Session List */}
                    <div className="p-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                        Completed Sessions ({phaseSessions.length})
                      </h4>

                      {phaseSessions.length === 0 ? (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No sessions completed yet
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {phaseSessions.map((session) => (
                            <SessionCard
                              key={session.id}
                              session={session}
                              isExpanded={expandedSession === session.id}
                              onToggle={() => toggleSession(session.id)}
                              formatDate={formatDate}
                              getWorkoutLabel={getWorkoutLabel}
                              getWorkoutColor={getWorkoutColor}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-gray-400 text-center py-2 border-t border-gray-100">
          * Optional sessions are not included in progress calculation
        </p>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Recent Activity</h2>
        </div>

        {recentActivity.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Dumbbell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>No activity yet. Start your first workout!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentActivity.map((activity, index) => (
              <ActivityItem key={index} activity={activity} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Session Card Component
function SessionCard({ session, isExpanded, onToggle, formatDate, getWorkoutLabel, getWorkoutColor }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Session Header */}
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
            <CheckCircle className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm">
                {getWorkoutLabel(session.workout)}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getWorkoutColor(session.workout)}`}>
                {getWorkoutLabel(session.workout)}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span className="flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {formatDate(session.completed_at)}
              </span>
              <span className="flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {session.duration_minutes} min
              </span>
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded Exercise Details */}
      {isExpanded && (
        <div className="px-3 pb-3 bg-gray-50 border-t border-gray-100">
          <div className="space-y-2 mt-3">
            {session.exercises?.supersets?.map((superset, idx) => (
              <SupersetDetail key={superset.superset_id || idx} superset={superset} index={idx} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Superset Detail Component
function SupersetDetail({ superset, index }) {
  const [expanded, setExpanded] = useState(false);
  const set1 = superset.sets?.set1;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center">
          <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mr-2 text-xs font-medium text-gray-600">
            {index + 1}
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900">{superset.exercise1_name}</p>
            <p className="text-xs text-gray-500">{superset.exercise2_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {superset.completed && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {superset.exercise1_name}
            </p>
            <ExerciseDataDisplay data={set1?.exercise1} />
          </div>

          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {superset.exercise2_name}
            </p>
            <ExerciseDataDisplay data={set1?.exercise2} />
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">All Sets</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(superset.sets || {}).map(([setKey, setData]) => (
                <SetSummary key={setKey} setKey={setKey} setData={setData} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Exercise Data Display Component
function ExerciseDataDisplay({ data }) {
  if (!data) return <p className="text-sm text-gray-400">No data recorded</p>;

  const displayItems = [];

  if (data.reps !== undefined) {
    displayItems.push({ label: 'Reps', value: data.reps });
  }
  if (data.weight !== undefined && data.weight !== '') {
    displayItems.push({ label: 'Weight', value: `${data.weight} lbs` });
  }
  if (data.level !== undefined && data.level !== '') {
    displayItems.push({ label: 'Level', value: data.level });
  }
  if (data.bandColor !== undefined && data.bandColor !== '') {
    displayItems.push({ label: 'Band', value: data.bandColor });
  }
  if (data.holdTime !== undefined) {
    displayItems.push({ label: 'Hold Time', value: `${data.holdTime}s` });
  }

  if (data.drill1 || data.drill2 || data.drill3) {
    const drills = [data.drill1, data.drill2, data.drill3].filter(Boolean);
    displayItems.push({ label: 'Drills', value: drills.join(', ') });
  }

  if (data.left || data.right) {
    if (data.left?.reps !== undefined) {
      displayItems.push({ label: 'Left Reps', value: data.left.reps });
    }
    if (data.right?.reps !== undefined) {
      displayItems.push({ label: 'Right Reps', value: data.right.reps });
    }
    if (data.left?.holdTime !== undefined) {
      displayItems.push({ label: 'Left Hold', value: `${data.left.holdTime}s` });
    }
    if (data.right?.holdTime !== undefined) {
      displayItems.push({ label: 'Right Hold', value: `${data.right.holdTime}s` });
    }
    if (data.left?.boxHeight !== undefined && data.left.boxHeight !== '') {
      displayItems.push({ label: 'Left Box Height', value: data.left.boxHeight });
    }
    if (data.right?.boxHeight !== undefined && data.right.boxHeight !== '') {
      displayItems.push({ label: 'Right Box Height', value: data.right.boxHeight });
    }
  }

  if (data.notes && data.notes.trim()) {
    displayItems.push({ label: 'Notes', value: data.notes });
  }

  if (displayItems.length === 0) {
    return <p className="text-sm text-gray-400">Completed</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {displayItems.map((item, idx) => (
        <div key={idx} className="bg-gray-100 rounded px-2 py-1">
          <span className="text-xs text-gray-500">{item.label}: </span>
          <span className="text-xs font-medium text-gray-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// Set Summary Component
function SetSummary({ setKey, setData }) {
  const setNum = setKey.replace('set', '');
  const ex1 = setData?.exercise1;
  const ex2 = setData?.exercise2;

  const getSummary = (data) => {
    if (!data) return '—';
    if (data.reps) return `${data.reps} reps`;
    if (data.left?.reps && data.right?.reps) return `L:${data.left.reps} R:${data.right.reps}`;
    if (data.holdTime) return `${data.holdTime}s`;
    if (data.left?.holdTime && data.right?.holdTime) return `L:${data.left.holdTime}s R:${data.right.holdTime}s`;
    if (data.drill1) return 'Drills ✓';
    return '✓';
  };

  return (
    <div className="bg-gray-50 rounded p-2 text-center">
      <p className="text-xs font-medium text-gray-600 mb-1">Set {setNum}</p>
      <p className="text-xs text-gray-500">{getSummary(ex1)}</p>
      <p className="text-xs text-gray-500">{getSummary(ex2)}</p>
    </div>
  );
}

function ActivityItem({ activity }) {
  const date = new Date(Number(activity.date));
  const isToday = date.toDateString() === new Date().toDateString();
  const isYesterday = date.toDateString() === new Date(Date.now() - 86400000).toDateString();

  const dateLabel = isToday 
    ? 'Today' 
    : isYesterday 
      ? 'Yesterday'
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const timeLabel = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });

  if (activity.type === 'testout') {
    return (
      <div className="p-4 flex items-center">
        <div className={`
          h-10 w-10 rounded-full flex items-center justify-center mr-3
          ${activity.passed ? 'bg-green-100' : 'bg-red-100'}
        `}>
          <ClipboardCheck className={`h-5 w-5 ${activity.passed ? 'text-green-600' : 'text-red-600'}`} />
        </div>
        
        <div className="flex-1">
          <p className="font-medium text-gray-900">
            Phase {activity.phase} Test Out
          </p>
          <p className="text-sm text-gray-500">
            {activity.passed ? 'Passed' : 'Not passed'} • {dateLabel} at {timeLabel}
          </p>
        </div>

        {activity.passed && (
          <Trophy className="h-5 w-5 text-yellow-500" />
        )}
      </div>
    );
  }

  const workoutLabel = activity.workout === 1 
    ? 'Workout I' 
    : activity.workout === 2 
      ? 'Workout II' 
      : 'Optional';

  return (
    <div className="p-4 flex items-center">
      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
        <Dumbbell className="h-5 w-5 text-primary" />
      </div>
      
      <div className="flex-1">
        <p className="font-medium text-gray-900">
          Phase {activity.phase} • {workoutLabel}
        </p>
        <p className="text-sm text-gray-500">
          {activity.data.duration_minutes || 0} min • {dateLabel} at {timeLabel}
        </p>
      </div>

      <CheckCircle className="h-5 w-5 text-green-500" />
    </div>
  );
}